import * as React from 'react';

import type { DropdownOptionType } from '..';

export interface FlatListProps<T extends DropdownOptionType> {
	options: T[] | null;
}

interface SharedProps<T extends DropdownOptionType> {
	calculatedPropName: keyof T;
	defaultValue?: Nullable<T>;
	filterOptions?: (option: T, index: number) => boolean;
	onChange: (item: string, options: T[]) => void;
	hasBlankOption: boolean;
	isLazyLoading: boolean;
	renderMenuItem: (item: Nullable<T>, index: number, searchText: string, onChangeHandler: (item: Nullable<string>) => void) => React.JSX.Element | null;
	searchText: string;
	selected: Nullable<T>;
	renderNoResults: () => React.JSX.Element;
	renderLoading: () => React.JSX.Element;
}

type Props<T extends DropdownOptionType> = FlatListProps<T> & SharedProps<T>;

interface State<T extends DropdownOptionType> {
	options: T[] | null;
}

class FlatListMenu<T extends DropdownOptionType> extends React.PureComponent<Props<T>, State<T>> {

	static getDerivedStateFromProps(props: Props<Record<string, unknown>>) {
		const { options, filterOptions } = props;

		if (filterOptions && options) {
			return { options: options.filter(filterOptions) };
		}
		return { options };
	}

	componentDidMount() {
		const { defaultValue, selected, calculatedPropName } = this.props;

		if (defaultValue && !selected) {
			this.handleChange(defaultValue[calculatedPropName]);
		}
	}

	handleChange = (item: string) => {
		const { options, onChange } = this.props;
		onChange(item, options!);
	};

	optionRenderItemMapper = (option: Nullable<T>, index: number) => {
		const { searchText, renderMenuItem } = this.props;
		return renderMenuItem(option, index, searchText, this.handleChange);
	};

	render() {
		const { hasBlankOption, isLazyLoading, renderMenuItem, searchText, renderNoResults, renderLoading } = this.props;
		const { options } = this.state;

		/**
		 * Basically, if the option is filtered out by the filter (in the dropdown component),
		 * it will return null, so we can filter it out here easily
		 */
		const optionsToRender = (options ?? []).map(this.optionRenderItemMapper);
		const isLoading = options === null || isLazyLoading;

		return (
			<>
				{hasBlankOption && !isLazyLoading && renderMenuItem(null, -1, searchText, this.handleChange)}
				{optionsToRender}
				{!optionsToRender.filter(Boolean).length && !hasBlankOption && !isLoading && renderNoResults()}
				{isLoading && renderLoading()}
			</>
		);
	}
}

export default FlatListMenu;
